<script setup lang="ts">
import EmptyState from '@/modules/Workspaces/KnowledgeHub/Files/EmptyState.vue'
import FileTableEmptyState from '@/modules/Workspaces/KnowledgeHub/Files/FileTableEmptyState.vue'
import { HubTable } from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable'
import type { TableSorting } from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/sorting'
import TreeNode from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/TreeNode.vue'
import {
  ancestorsOf,
  buildFolderTree,
  sortTree,
  type FolderNode,
} from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTree/tree'
import { useHubFileTableContext } from '@/modules/Workspaces/KnowledgeHub/Hubs/hubFileTableContext'
import HubFileTableSkeleton from '@/modules/Workspaces/KnowledgeHub/Hubs/HubFileTableSkeleton.vue'

import type { HubFile } from '@/modules/Workspaces/KnowledgeHub/Hubs/types'
import { useHubFileSearch } from '@/modules/Workspaces/KnowledgeHub/Hubs/useHubFileSearch'
import ScrollContainer from '@/uiKit/ScrollContainer/ScrollContainer.vue'
import * as zagTree from '@zag-js/tree-view'
import { normalizeProps, useMachine } from '@zag-js/vue'
import { computed, ref, toRef, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    files: HubFile[]
    search?: string
    /** If true, table will display placeholder rows */
    isLoading?: boolean
  }>(),
  {
    search: '',
    isLoading: false,
  },
)

const context = useHubFileTableContext()

const items = computed(() => props.files)
const searchTerm = toRef(props, 'search')

const sort = ref<TableSorting>(['createdAt', 'desc'])

const { filteredFiles, debouncedSearchTerm } = useHubFileSearch({
  searchTerm,
  files: items,
})

// ---------------------------------------------------------------------------
// Build collection and create zag tree machine
// ---------------------------------------------------------------------------

const rootNode = computed<FolderNode>(() => {
  const root = buildFolderTree(filteredFiles.value)
  sortTree(root, sort.value)

  return root
})

const collection = computed(() => {
  return zagTree.collection({
    nodeToValue: (node) => node.id,
    nodeToString: (node) => node.name,
    rootNode: rootNode.value,
  })
})

const treeMachine = useMachine(
  zagTree.machine,
  computed(() => ({
    id: 'hub-file-table',
    collection: collection.value,
    defaultExpandedValue: ['folder:/'],
    selectionMode: 'multiple' as const,
    expandOnClick: true,
    onSelectionChange(details) {
      if (details.selectedValue.length === api.value.collection.getValues().length) {
        for (const file of props.files) context.setSelected(file.entity.id, true)
      }
    },
  })),
)
const api = computed(() => zagTree.connect(treeMachine, normalizeProps))

const hasNoSearchResults = computed(
  () => items.value.length > 0 && filteredFiles.value.length === 0,
)

const showHeader = computed(
  () => props.isLoading || (items.value.length > 0 && !hasNoSearchResults.value),
)

// Auto-expand folders that contain search hits
watch(
  () => debouncedSearchTerm.value,
  (term) => {
    if (!term) return
    const ids: string[] = []
    filteredFiles.value.forEach((file) => {
      ancestorsOf(file.fields.path.manualValue).forEach((p) => ids.push(`folder:${p}`))
    })
    api.value.expand(ids)
  },
)

/**
 * Zag tree would capture all keydowns on a row and we wouldn't be able to select items,
 * or trigger any of the buttons programatically - so we need to patch the keydown event.
 */
const treeProps = computed(() => {
  const ogProps = api.value.getTreeProps()
  const patchedKeydown = (ev: KeyboardEvent) => {
    if (ev.code === 'Enter' || ev.code === 'Space') {
      if (ev.target instanceof HTMLButtonElement) {
        return
      }

      if (ev.target instanceof HTMLElement) {
        const treeItem = ev.target.closest(`[role="treeitem"][data-ownedby="${ogProps.id}"]`)
        if (treeItem instanceof HTMLElement) {
          const id = treeItem.dataset.value?.replace('file:', '')
          if (id) {
            context.setSelected(id, !context.isSelected(id))
          }
        }
      }
    }

    ogProps.onKeydown?.(ev)
  }

  return { ...ogProps, onKeydown: patchedKeydown }
})
</script>

<template>
  <div
    class="flex shrink grow flex-col overflow-hidden"
    data-test="hub-file-table"
  >
    <!-- Empty state - no files -->
    <FileTableEmptyState
      v-if="items.length === 0 && !isLoading"
      class="mt-10"
    />

    <!-- No search results -->
    <EmptyState v-else-if="hasNoSearchResults && !isLoading">
      <template #title> No result found </template>
      <template #description> We could not find any file matching your search. </template>
    </EmptyState>

    <!-- Table with header -->
    <template v-else>
      <HubTable.Header
        v-if="showHeader"
        v-model:sort="sort"
      />

      <ScrollContainer
        v-bind="api.getRootProps()"
        fade-size="md"
        :disabled="isLoading"
      >
        <HubFileTableSkeleton v-if="isLoading" />
        <div
          v-else
          v-bind="treeProps"
          role="list"
        >
          <TreeNode
            v-for="(node, index) in api.collection.rootNode.children"
            :key="node.id"
            :node="node"
            :index-path="[index]"
            :api="api"
          />
        </div>
      </ScrollContainer>
    </template>
  </div>
</template>
